<template>
    <div class="profile-page" v-if="profile">
        <div class="profile-sheet">
            <header class="profile-head">
                <button type="button" class="icon-btn" @click="$router.back()" aria-label="Voltar">
                    <img src="images/icons/arrow-left.svg" alt="" width="20" height="20" />
                </button>
                <div class="identity">
                    <button type="button" class="avatar-btn" @click="$refs.photo.click()">
                        <img v-if="profile.logo" :src="profile.logo" alt="" />
                        <span v-else class="initials">{{ initials(profile.name) }}</span>
                        <span class="pencil" aria-hidden="true"><img src="images/icons/edit-02.svg" alt="" width="16" height="16" /></span>
                    </button>
                    <input ref="photo" type="file" accept="image/png,image/jpeg,image/webp,image/gif" class="sr" @change="uploadOwnPhoto" />
                    <div class="identity-text">
                        <p class="kicker">{{ t('users-profile-title') }}</p>
                        <h1>{{ profile.name }}</h1>
                    </div>
                </div>
                <span class="head-spacer" aria-hidden="true"></span>
            </header>

            <section class="card personal-card">
                <div class="card-head">
                    <h2>{{ t('users-profile-personal') }}</h2>
                    <p class="help">{{ t('users-profile-personal_help') }}</p>
                </div>
                <div class="divider" aria-hidden="true"></div>
                <div class="row">
                    <div class="row-main">
                        <img class="row-icon" src="images/icons/user-03.svg" alt="" />
                        <strong>{{ t('users-create-name') }}</strong>
                    </div>
                    <button type="button" class="ghost" @click="openName">{{ t('users-profile-change') }}</button>
                </div>
            </section>

            <section class="card security-card" :id="securityAnchor">
                <div class="security-head">
                    <img v-if="needsAttention" class="row-icon" src="images/icons/alert-triangle.svg" alt="" />
                    <img v-else class="row-icon" src="images/icons/shield-tick.svg" alt="" />
                    <div>
                        <h2>{{ t('users-profile-security') }}</h2>
                        <p class="help">{{ t('users-profile-security_help') }}</p>
                    </div>
                </div>

                <div v-if="needsAttention" class="warning">
                    <strong>{{ t('users-profile-warning_title') }}</strong>
                    <p>{{ t('users-profile-warning_body') }}</p>
                </div>

                <div class="recovery">
                    <div class="card-head tight">
                        <h3>{{ t('users-profile-recovery') }}</h3>
                        <p class="help">{{ t('users-profile-recovery_help') }}</p>
                    </div>

                    <div class="row flush">
                        <div class="row-main">
                            <img class="row-icon" src="images/icons/mail-02.svg" alt="" />
                            <div class="info">
                                <div class="title-line">
                                    <strong>{{ t('users-create-email') }}</strong>
                                    <em v-if="profile.email" :class="profile.email_verified ? 'ok' : 'wait'">{{ profile.email_verified ? t('users-profile-verified') : t('users-profile-unverified') }}</em>
                                </div>
                                <p>{{ profile.email || t('users-no_email') }}</p>
                            </div>
                        </div>
                        <div class="actions">
                            <button v-if="profile.email" type="button" class="ghost" @click="startContact('email', 'change')">{{ t('users-profile-change') }}</button>
                            <button v-if="profile.email && !profile.email_verified" type="button" class="primary" @click="startContact('email', 'verify')">{{ t('users-profile-verify') }}</button>
                            <button v-else-if="!profile.email" type="button" class="ghost with-icon" @click="startContact('email', 'add')">
                                <img src="images/icons/plus.svg" alt="" width="16" height="16" />
                                {{ t('users-profile-add_email') }}
                            </button>
                        </div>
                    </div>

                    <div class="row flush">
                        <div class="row-main">
                            <img class="row-icon" src="images/icons/phone.svg" alt="" />
                            <div class="info">
                                <div class="title-line">
                                    <strong>{{ t('users-create-phone') }}</strong>
                                    <em v-if="profile.phone" :class="profile.phone_verified ? 'ok' : 'wait'">{{ profile.phone_verified ? t('users-profile-verified') : t('users-profile-unverified') }}</em>
                                </div>
                                <p :class="{ muted: !profile.phone }">{{ profile.phone || t('users-profile-no_phone') }}</p>
                            </div>
                        </div>
                        <div class="actions">
                            <button v-if="profile.phone" type="button" class="ghost" @click="startContact('phone', 'change')">{{ t('users-profile-change') }}</button>
                            <button v-if="profile.phone && !profile.phone_verified" type="button" class="primary" @click="startContact('phone', 'verify')">{{ t('users-profile-verify') }}</button>
                            <button v-else-if="!profile.phone" type="button" class="ghost with-icon" @click="startContact('phone', 'add')">
                                <img src="images/icons/plus.svg" alt="" width="16" height="16" />
                                {{ t('users-profile-add_phone') }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="divider" aria-hidden="true"></div>

                <div class="row flush">
                    <div class="row-main">
                        <img class="row-icon" src="images/icons/shield-tick.svg" alt="" />
                        <div class="info">
                            <div class="title-line">
                                <strong>{{ t('users-profile-twofa') }}</strong>
                                <em :class="profile.twofa_enabled ? 'ok' : 'off'">{{ profile.twofa_enabled ? t('users-profile-active') : t('users-profile-inactive') }}</em>
                            </div>
                            <p class="muted two-lines">{{ t('users-profile-twofa_help') }}</p>
                        </div>
                    </div>
                    <button v-if="!profile.twofa_enabled" type="button" class="primary" @click="openTwofa('password')">{{ t('users-profile-enable') }}</button>
                    <button v-else type="button" class="ghost" @click="openTwofa('disable')">{{ t('users-profile-disable') }}</button>
                </div>

                <div class="divider" aria-hidden="true"></div>

                <div class="row flush">
                    <div class="row-main">
                        <img class="row-icon" src="images/icons/key-01.svg" alt="" />
                        <div class="info">
                            <strong>{{ t('users-profile-password') }}</strong>
                            <p class="muted">{{ profile.password_changed_at ? t('users-profile-password_changed', { 0: formatDate(profile.password_changed_at) }) : t('users-profile-password_unknown') }}</p>
                        </div>
                    </div>
                    <button type="button" class="ghost" @click="openPassword">{{ t('users-profile-change_password') }}</button>
                </div>

                <p v-if="error" class="form-error">{{ error }}</p>
            </section>

            <p class="note"><img src="images/icons/info-circle.svg" alt="" width="16" height="16" />{{ t('users-profile-note') }}</p>

            <section class="card logout-card">
                <div class="row flush">
                    <div class="row-main">
                        <img class="row-icon" src="images/icons/log-out-01.svg" alt="" />
                        <div class="info">
                            <strong>{{ t('users-profile-logout_title') }}</strong>
                            <p class="muted">{{ t('users-profile-logout_help') }}</p>
                        </div>
                    </div>
                    <button type="button" class="ghost" @click="logout">{{ t('users-profile-logout') }}</button>
                </div>
            </section>
        </div>

        <modal ref="nameModal" size="small" :show-close="false" class="account-form-host">
            <template slot="body">
                <div class="account-form-flow">
                    <div class="account-form-head">
                        <div class="contact-badge">
                            <img src="images/icons/user-03-green.svg" alt="" />
                        </div>
                        <h2 class="contact-title">{{ t('users-profile-name_title') }}</h2>
                        <p class="contact-subtitle">{{ t('users-profile-name_help') }}</p>
                    </div>
                    <div class="contact-fields">
                        <div class="contact-field">
                            <label>{{ t('users-create-name') }}</label>
                            <div class="contact-input">
                                <input v-model="name" type="text" autocomplete="name" />
                            </div>
                        </div>
                        <div class="contact-field">
                            <label>{{ t('users-profile-current_password') }}</label>
                            <div class="contact-input">
                                <input
                                    v-model="currentPassword"
                                    :type="showPassword ? 'text' : 'password'"
                                    autocomplete="current-password"
                                />
                                <button type="button" class="contact-eye" @click="showPassword = !showPassword" :aria-label="showPassword ? t('users-edit-hide') : t('users-edit-show')">
                                    <img src="images/icons/eye.svg" alt="" />
                                </button>
                            </div>
                        </div>
                        <p v-if="error" class="contact-error-text">{{ error }}</p>
                    </div>
                    <div class="contact-footer">
                        <button type="button" class="contact-btn contact-btn-ghost" @click="$refs.nameModal.close()">{{ t('users-profile-cancel') }}</button>
                        <button type="button" class="contact-btn contact-btn-primary" @click="saveName">{{ t('users-profile-save') }}</button>
                    </div>
                </div>
            </template>
        </modal>

        <modal ref="contactModal" size="small" :show-close="false" class="contact-host">
            <template slot="body">
                <div class="contact-flow">
                    <div class="contact-head">
                        <div class="contact-badge">
                            <img :src="contactType === 'phone' ? 'images/icons/phone-green.svg' : 'images/icons/mail-02-green.svg'" alt="" />
                        </div>
                        <h2 class="contact-title">{{ contactTitle }}</h2>
                        <p class="contact-subtitle">{{ contactPhase === 'code' ? contactCodeHelp : contactHelp }}</p>
                    </div>

                    <div v-if="contactPhase === 'form'" class="contact-fields">
                        <template v-if="contactAction !== 'verify'">
                            <div class="contact-field">
                                <label>{{ contactType === 'email' ? t('users-profile-your_email') : t('users-profile-your_phone') }}</label>
                                <div class="contact-input">
                                    <template v-if="contactType === 'email'">
                                        <img src="images/icons/mail-02.svg" alt="" class="contact-input-icon" />
                                        <input v-model="contactValue" type="email" :placeholder="t('users-profile-your_email')" autocomplete="off" />
                                    </template>
                                    <input
                                        v-else
                                        v-model="contactValue"
                                        v-mask="['(##) ####-####', '(##) # ####-####']"
                                        type="tel"
                                        inputmode="numeric"
                                        :placeholder="t('users-profile-phone_placeholder')"
                                        autocomplete="off"
                                    />
                                </div>
                            </div>
                            <div class="contact-field">
                                <label>{{ contactType === 'email' ? t('users-profile-confirm_email') : t('users-profile-confirm_phone') }}</label>
                                <div class="contact-input">
                                    <template v-if="contactType === 'email'">
                                        <img src="images/icons/mail-02.svg" alt="" class="contact-input-icon" />
                                        <input v-model="contactConfirm" type="email" :placeholder="t('users-profile-confirm_email')" autocomplete="off" />
                                    </template>
                                    <input
                                        v-else
                                        v-model="contactConfirm"
                                        v-mask="['(##) ####-####', '(##) # ####-####']"
                                        type="tel"
                                        inputmode="numeric"
                                        :placeholder="t('users-profile-phone_placeholder')"
                                        autocomplete="off"
                                    />
                                </div>
                            </div>
                        </template>
                        <div v-if="contactAction !== 'add'" class="contact-field">
                            <label>{{ t('users-profile-current_password') }}</label>
                            <div class="contact-input">
                                <input
                                    v-model="currentPassword"
                                    :type="showPassword ? 'text' : 'password'"
                                    autocomplete="current-password"
                                />
                                <button type="button" class="contact-eye" @click="showPassword = !showPassword" :aria-label="showPassword ? t('users-edit-hide') : t('users-edit-show')">
                                    <img src="images/icons/eye.svg" alt="" />
                                </button>
                            </div>
                        </div>
                        <p v-if="error" class="contact-error-text">{{ error }}</p>
                    </div>

                    <div v-else class="contact-otp-wrap">
                        <div class="contact-otp" :class="{ 'is-error': codeError }">
                            <input
                                v-for="(digit, index) in contactOtpDigits"
                                :key="'contact-otp-' + index"
                                :ref="'contactOtp' + index"
                                type="text"
                                inputmode="numeric"
                                maxlength="1"
                                class="contact-otp-digit"
                                :class="{ 'is-filled': !!digit, 'is-active': contactOtpFocus === index && !codeError, 'is-error': codeError }"
                                :value="digit"
                                :placeholder="digit ? '' : '0'"
                                @focus="contactOtpFocus = index"
                                @input="onContactOtpInput($event, index)"
                                @keydown="onContactOtpKeydown($event, index)"
                                @paste="onContactOtpPaste"
                            />
                        </div>
                        <p v-if="!codeError && resendIn > 0" class="contact-hint">{{ t('users-profile-resend_in', { 0: resendLabel }) }}</p>
                        <button v-else-if="!codeError" type="button" class="contact-resend is-link" @click="sendContact">{{ t('users-profile-resend') }}</button>
                        <template v-else>
                            <p class="contact-error-msg">
                                <img src="images/icons/alert-circle-red.svg" alt="" />
                                {{ codeErrorMessage || t('users-profile-invalid_code') }}
                            </p>
                            <button type="button" class="contact-resend is-link" @click="sendContact">{{ t('users-profile-resend') }}</button>
                        </template>
                        <p v-if="error" class="contact-error-text">{{ error }}</p>
                    </div>

                    <div class="contact-footer">
                        <button
                            type="button"
                            class="contact-btn contact-btn-ghost"
                            @click="contactPhase === 'code' ? (contactPhase = 'form') : $refs.contactModal.close()"
                        >{{ contactPhase === 'code' ? t('users-edit-back') : t('users-profile-cancel') }}</button>
                        <button
                            v-if="contactPhase === 'form'"
                            type="button"
                            class="contact-btn contact-btn-primary"
                            @click="sendContact"
                        >{{ t('users-profile-send_code') }}</button>
                        <button
                            v-else
                            type="button"
                            class="contact-btn contact-btn-primary"
                            :disabled="!canConfirmContact"
                            :class="{ 'is-disabled': !canConfirmContact }"
                            @click="confirmContact"
                        >{{ contactSubmitting ? t('users-password_modal-submitting') : t('users-profile-confirm') }}</button>
                    </div>
                </div>
            </template>
        </modal>

        <modal ref="passwordModal" size="small" :show-close="false" class="account-form-host">
            <template slot="body">
                <div class="account-form-flow">
                    <div class="account-form-head">
                        <div class="contact-badge">
                            <img src="images/icons/key-01-green.svg" alt="" />
                        </div>
                        <h2 class="contact-title">{{ t('users-profile-password_title') }}</h2>
                        <p class="contact-subtitle">{{ t('users-profile-password_rule') }}</p>
                    </div>
                    <div class="contact-fields">
                        <div class="contact-field">
                            <label>{{ t('users-profile-current_password') }}</label>
                            <div class="contact-input">
                                <input
                                    v-model="currentPassword"
                                    :type="showPassword ? 'text' : 'password'"
                                    autocomplete="current-password"
                                />
                                <button type="button" class="contact-eye" @click="showPassword = !showPassword" :aria-label="showPassword ? t('users-edit-hide') : t('users-edit-show')">
                                    <img src="images/icons/eye.svg" alt="" />
                                </button>
                            </div>
                        </div>
                        <div class="contact-field">
                            <label>{{ t('users-profile-new_password') }}</label>
                            <div class="contact-input">
                                <input v-model="newPassword" type="password" autocomplete="new-password" />
                            </div>
                        </div>
                        <div class="contact-field">
                            <label>{{ t('users-profile-repeat_password') }}</label>
                            <div class="contact-input">
                                <input v-model="repeatPassword" type="password" autocomplete="new-password" />
                            </div>
                        </div>
                        <p v-if="error" class="contact-error-text">{{ error }}</p>
                    </div>
                    <div class="contact-footer">
                        <button type="button" class="contact-btn contact-btn-ghost" @click="$refs.passwordModal.close()">{{ t('users-profile-cancel') }}</button>
                        <button type="button" class="contact-btn contact-btn-primary" @click="savePassword">{{ t('users-profile-change_password_button') }}</button>
                    </div>
                </div>
            </template>
        </modal>

        <modal ref="twofaModal" size="small" :show-close="false" class="twofa-host">
            <template slot="body">
                <div class="twofa-flow">
                    <div class="twofa-head">
                        <div class="twofa-badge" :class="{ 'is-danger': twofaDanger }">
                            <img :src="twofaDanger ? 'images/icons/shield-off-red.svg' : 'images/icons/shield-tick-green.svg'" alt="" />
                        </div>
                        <p v-if="twofaStepLabel" class="twofa-step">{{ twofaStepLabel }}</p>
                        <h2 class="twofa-title">{{ twofaTitle }}</h2>
                        <p class="twofa-subtitle">{{ twofaSubtitle }}</p>
                    </div>

                    <div v-if="twofaStep === 'disable'" class="twofa-warning" role="note">
                        <img src="images/icons/alert-circle-red.svg" alt="" />
                        <span>{{ t('users-profile-disable_warning') }}</span>
                    </div>

                    <div v-if="twofaStep === 'password' || twofaStep === 'disable'" class="twofa-field">
                        <label>{{ t('users-profile-current_password') }}</label>
                        <div class="twofa-input">
                            <input
                                v-model="currentPassword"
                                :type="showPassword ? 'text' : 'password'"
                                autocomplete="current-password"
                            />
                            <button type="button" class="twofa-eye" @click="showPassword = !showPassword" :aria-label="showPassword ? t('users-edit-hide') : t('users-edit-show')">
                                <img src="images/icons/eye.svg" alt="" />
                            </button>
                        </div>
                        <p v-if="error" class="twofa-error-text">{{ error }}</p>
                    </div>

                    <div v-else-if="twofaStep === 'qr'" class="twofa-qr">
                        <div class="twofa-qr-frame">
                            <img v-if="qrImage" :src="qrImage" :alt="t('users-profile-qr_alt')" />
                        </div>
                        <p class="twofa-manual-help">{{ t('users-profile-manual_key') }}</p>
                        <div class="twofa-secret">
                            <span>{{ formattedSecret }}</span>
                            <button type="button" class="twofa-link" @click="copy(secret)">{{ copied ? t('users-profile-copied') : t('users-profile-copy') }}</button>
                        </div>
                    </div>

                    <div v-else-if="twofaStep === 'code'" class="twofa-otp-wrap">
                        <div class="twofa-otp" :class="{ 'is-error': codeError }">
                            <input
                                v-for="(digit, index) in otpDigits"
                                :key="'otp-' + index"
                                :ref="'otp' + index"
                                type="text"
                                inputmode="numeric"
                                maxlength="1"
                                class="twofa-otp-digit"
                                :class="{ 'is-filled': !!digit, 'is-active': otpFocus === index && !codeError, 'is-error': codeError }"
                                :value="digit"
                                :placeholder="digit ? '' : '0'"
                                @focus="otpFocus = index"
                                @input="onOtpInput($event, index)"
                                @keydown="onOtpKeydown($event, index)"
                                @paste="onOtpPaste"
                            />
                        </div>
                        <p v-if="!codeError" class="twofa-hint">{{ t('users-profile-app_code_hint') }}</p>
                        <p v-else class="twofa-error-msg">
                            <img src="images/icons/alert-circle-red.svg" alt="" />
                            {{ t('users-profile-app_code_error') }}
                        </p>
                    </div>

                    <div v-else-if="twofaStep === 'codes'" class="twofa-codes-layout">
                        <button type="button" class="twofa-btn twofa-btn-primary twofa-codes-primary" @click="finishTwofa">{{ t('users-profile-saved_codes') }}</button>
                        <div class="twofa-codes-content">
                            <div class="twofa-codes-box">
                                <div class="twofa-codes-col">
                                    <span v-for="item in backupCodesLeft" :key="'l-' + item">{{ item }}</span>
                                </div>
                                <div class="twofa-codes-col">
                                    <span v-for="item in backupCodesRight" :key="'r-' + item">{{ item }}</span>
                                </div>
                            </div>
                            <div class="twofa-codes-actions">
                                <button type="button" class="twofa-btn twofa-btn-ghost twofa-btn-sm" @click="copy(backupCodes.join('\n'))">{{ copied ? t('users-profile-copied') : t('users-profile-copy_codes') }}</button>
                                <button type="button" class="twofa-btn twofa-btn-ghost twofa-btn-sm" @click="downloadCodes">
                                    <img src="images/icons/download-cloud-02.svg" alt="" />
                                    {{ t('users-profile-download') }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="twofaStep === 'disabled'" class="twofa-disabled-body"></div>

                    <div v-if="twofaStep === 'password'" class="twofa-footer">
                        <button type="button" class="twofa-btn twofa-btn-ghost" @click="$refs.twofaModal.close()">{{ t('users-profile-cancel') }}</button>
                        <button type="button" class="twofa-btn twofa-btn-primary" :disabled="twofaSubmitting" @click="confirmForTwofa">{{ t('users-profile-continue') }}</button>
                    </div>
                    <div v-else-if="twofaStep === 'qr'" class="twofa-footer">
                        <button type="button" class="twofa-btn twofa-btn-ghost" @click="twofaStep = 'password'">{{ t('users-edit-back') }}</button>
                        <button type="button" class="twofa-btn twofa-btn-primary" @click="goToCodeStep">{{ t('users-profile-continue') }}</button>
                    </div>
                    <div v-else-if="twofaStep === 'code'" class="twofa-footer">
                        <button type="button" class="twofa-btn twofa-btn-ghost" @click="twofaStep = 'qr'">{{ t('users-edit-back') }}</button>
                        <button type="button" class="twofa-btn twofa-btn-primary" :disabled="!canActivateTwofa" :class="{ 'is-disabled': !canActivateTwofa }" @click="activateTwofa">{{ t('users-profile-activate') }}</button>
                    </div>
                    <div v-else-if="twofaStep === 'disable'" class="twofa-footer">
                        <button type="button" class="twofa-btn twofa-btn-ghost" @click="$refs.twofaModal.close()">{{ t('users-profile-cancel') }}</button>
                        <button type="button" class="twofa-btn twofa-btn-danger" @click="disableTwofa">{{ t('users-profile-disable_button') }}</button>
                    </div>
                    <div v-else-if="twofaStep === 'disabled'" class="twofa-footer twofa-footer-disabled">
                        <button type="button" class="twofa-btn twofa-btn-primary" @click="restartTwofa">{{ t('users-profile-enable_again') }}</button>
                        <button type="button" class="twofa-btn twofa-btn-ghost" @click="$refs.twofaModal.close()">{{ t('users-profile-close') }}</button>
                    </div>
                </div>
            </template>
        </modal>

        <confirm id="confirm_profile_logout" ref="confirmLogout" @confirm="$router.push('/logout')">
            <template slot="title">{{ t('header-logout_title') }}</template>
            <template slot="body">{{ t('header-logout_message') }}</template>
            <template slot="cancel">{{ t('header-no') }}</template>
            <template slot="confirm">{{ t('header-yes') }}</template>
        </confirm>
    </div>
</template>

<script>
import keviApi from "../keviApi";
import { uploadFilesToStorage } from "../utils/upload-helper";

export default {
    name: "account-profile",
    data() {
        return {
            profile: null,
            name: "",
            currentPassword: "",
            newPassword: "",
            repeatPassword: "",
            contactType: "email",
            contactAction: "add",
            contactPhase: "form",
            contactValue: "",
            contactConfirm: "",
            code: "",
            masked: "",
            resendIn: 0,
            resendTimer: null,
            codeError: false,
            error: "",
            twofaStep: "password",
            qrImage: "",
            secret: "",
            grantToken: "",
            copied: false,
            copiedTimer: null,
            twofaSubmitting: false,
            backupCodes: [],
            otpDigits: ["", "", "", "", "", ""],
            otpFocus: 0,
            twofaCode: "",
            contactOtpDigits: ["", "", "", "", "", ""],
            contactOtpFocus: 0,
            contactSubmitting: false,
            codeErrorMessage: "",
            showPassword: false,
            securityAnchor: "security",
            photoUploading: false,
        };
    },
    computed: {
        needsAttention() {
            if (!this.profile) return false;
            return !this.profile.email_verified || !this.profile.phone_verified || !this.profile.twofa_enabled;
        },
        contactTitle() {
            if (this.contactPhase === "code") return this.t("users-profile-code_title");
            const email = this.contactType === "email";
            if (this.contactAction === "change") {
                return email ? this.t("users-profile-change_email") : this.t("users-profile-change_phone");
            }
            if (this.contactAction === "verify") {
                return email ? this.t("users-profile-verify_email") : this.t("users-profile-verify_phone");
            }
            return email ? this.t("users-profile-add_email") : this.t("users-profile-add_phone");
        },
        contactHelp() {
            const email = this.contactType === "email";
            if (this.contactAction === "change") return email ? this.t("users-profile-change_email_help") : this.t("users-profile-change_phone_help");
            if (this.contactAction === "verify") return email ? this.t("users-profile-verify_email_help") : this.t("users-profile-verify_phone_help");
            return email ? this.t("users-profile-add_email_help") : this.t("users-profile-add_phone_help");
        },
        contactDestination() {
            // Em adicionar/alterar a pessoa acabou de digitar o contato: mostramos por extenso.
            if (this.contactAction === "verify") return this.masked;
            if (this.contactType === "phone") return this.formatPhone(this.contactValue) || this.masked;
            return String(this.contactValue || "").trim() || this.masked;
        },
        contactCodeHelp() {
            if (this.contactType === "phone") return this.t("users-profile-code_sms", { 0: this.contactDestination });
            return this.t("users-profile-code_help", { 0: this.contactDestination });
        },
        canConfirmContact() {
            return !this.codeError && !this.contactSubmitting && this.code.length === 6;
        },
        resendLabel() {
            const seconds = this.resendIn % 60;
            const minutes = Math.floor(this.resendIn / 60);
            return minutes + ":" + String(seconds).padStart(2, "0");
        },
        twofaTitle() {
            const map = {
                password: "users-profile-confirm_password_title",
                qr: "users-profile-qr_title",
                code: "users-profile-app_code",
                codes: "users-profile-codes_title",
                disable: "users-profile-disable_title",
                disabled: "users-profile-disabled_title",
            };
            return this.t(map[this.twofaStep] || "users-profile-twofa");
        },
        twofaSubtitle() {
            const map = {
                password: "users-profile-confirm_password_help",
                qr: "users-profile-qr_help",
                code: "users-profile-app_code_help",
                codes: "users-profile-codes_help",
                disable: "users-profile-disable_body",
                disabled: "users-profile-disabled_body",
            };
            return this.t(map[this.twofaStep] || "users-profile-twofa_help");
        },
        twofaStepLabel() {
            if (this.twofaStep === "qr") return this.t("users-profile-qr_step");
            if (this.twofaStep === "code") return this.t("users-profile-code_step");
            if (this.twofaStep === "codes") return this.t("users-profile-codes_step");
            return "";
        },
        formattedSecret() {
            return String(this.secret || "").replace(/\s/g, "").replace(/(.{4})/g, "$1 ").trim();
        },
        backupCodesLeft() {
            const mid = Math.ceil(this.backupCodes.length / 2);
            return this.backupCodes.slice(0, mid);
        },
        backupCodesRight() {
            const mid = Math.ceil(this.backupCodes.length / 2);
            return this.backupCodes.slice(mid);
        },
        canActivateTwofa() {
            return !this.codeError && !this.twofaSubmitting && this.twofaCode.length === 6;
        },
        twofaDanger() {
            return this.twofaStep === "disable" || this.twofaStep === "disabled";
        },
    },
    created() {
        document.title = "Kevi | " + this.t("users-profile-title");
        this.load();
    },
    beforeDestroy() {
        clearInterval(this.resendTimer);
        clearTimeout(this.copiedTimer);
    },
    watch: {
        "$route.query.action": {
            immediate: true,
            handler() {
                this.openFromQuery();
            },
        },
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        load() {
            this.$http(keviApi.getOptions("account/profile")).then((response) => {
                this.profile = response.body;
                this.name = response.body.name;
                this.$nextTick(this.openFromQuery);
            });
        },
        openFromQuery() {
            if (!this.profile) return;
            const action = this.$route.query.action;
            if (!action) return;
            if (action === "add-email") this.startContact("email", "add");
            if (action === "add-phone") this.startContact("phone", "add");
            if (action === "confirm-email") this.startContact("email", "verify");
            if (action === "confirm-phone") this.startContact("phone", "verify");
            if (action === "change-email") this.startContact("email", "change");
            if (action === "change-phone") this.startContact("phone", "change");
            if (action === "enable-2fa") {
                this.openTwofa("password");
            }
            if (action === "security" && this.$el && this.$el.querySelector) {
                const section = this.$el.querySelector(".security-card");
                if (section) section.scrollIntoView();
            }
        },
        formatDate(value) {
            const date = new Date(String(value).replace(" ", "T"));
            if (Number.isNaN(date.getTime())) return value;
            const locales = { ptBr: "pt-BR", en: "en-US", es: "es-ES" };
            const locale = locales[this.$root.lang] || "pt-BR";
            return date.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
        },
        formatPhone(value) {
            const digits = String(value || "").replace(/\D+/g, "");
            if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(3, 7)}-${digits.slice(7)}`;
            if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
            return digits;
        },
        passwordError(response, fallback) {
            const code = response && response.body && response.body.message;
            if (response && response.status === 429) return this.t("users-profile-too_many_attempts");
            if (code === "too_many_attempts") return this.t("users-profile-too_many_attempts");
            if (code === "wrong_password") return this.t("users-profile-wrong_password");
            return this.t(fallback || "users-profile-generic_error");
        },
        initials(name) {
            return (name || "?").split(" ").filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
        },
        async uploadOwnPhoto(event) {
            const input = event.target;
            const file = input.files && input.files[0];
            if (!file || this.photoUploading) return;
            this.photoUploading = true;
            this.error = "";
            try {
                const urls = await uploadFilesToStorage([file], this.t.bind(this));
                const url = urls && urls[0];
                if (!url) throw new Error("empty");
                const saved = await this.$http(keviApi.getOptions("account/logo", "POST", { logo: url }));
                this.profile = saved.body;
                this.$store.dispatch("notification/success", this.t("users-profile-photo_saved"));
            } catch (err) {
                this.error = this.t("users-profile-photo_failed");
                this.$store.dispatch("notification/warning", this.t("users-profile-photo_failed"));
            } finally {
                this.photoUploading = false;
                if (input) input.value = "";
            }
        },
        openName() {
            this.error = "";
            this.currentPassword = "";
            this.showPassword = false;
            this.name = this.profile.name;
            this.$refs.nameModal.open();
        },
        saveName() {
            if (!String(this.name || "").trim()) {
                this.error = this.t("users-profile-name_required");
                return;
            }
            this.error = "";
            this.$http(keviApi.getOptions("account/name", "POST", {
                name: this.name,
                current_password: this.currentPassword,
            })).then(
                (response) => {
                    this.profile = response.body;
                    this.$refs.nameModal.close();
                    this.$store.dispatch("notification/success", this.t("users-profile-name_toast"));
                },
                (response) => { this.error = this.passwordError(response); }
            );
        },
        startContact(type, action) {
            this.contactType = type;
            this.contactAction = action;
            this.contactPhase = "form";
            this.contactValue = action === "verify" ? (type === "email" ? this.profile.email : this.profile.phone) : "";
            this.contactConfirm = "";
            this.code = "";
            this.error = "";
            this.codeError = false;
            this.currentPassword = "";
            this.showPassword = false;
            this.contactSubmitting = false;
            this.resetContactOtp();
            this.$nextTick(() => this.$refs.contactModal.open());
        },
        normalizePhoneInput(value) {
            return String(value || "").replace(/\D+/g, "");
        },
        contactsMatch() {
            if (this.contactType === "phone") {
                const a = this.normalizePhoneInput(this.contactValue);
                const b = this.normalizePhoneInput(this.contactConfirm);
                return a !== "" && a === b;
            }
            return String(this.contactValue || "").trim().toLowerCase()
                === String(this.contactConfirm || "").trim().toLowerCase()
                && String(this.contactValue || "").trim() !== "";
        },
        sendContact() {
            if (this.contactPhase === "form" && this.contactAction !== "verify" && !this.contactsMatch()) {
                this.error = this.t("users-profile-contact_mismatch");
                return;
            }
            this.error = "";
            this.codeError = false;
            this.contactSubmitting = false;
            const value = this.contactType === "phone"
                ? this.normalizePhoneInput(this.contactValue)
                : String(this.contactValue || "").trim();
            const confirmValue = this.contactAction === "verify"
                ? value
                : (this.contactType === "phone"
                    ? this.normalizePhoneInput(this.contactConfirm)
                    : String(this.contactConfirm || "").trim());
            this.$http(keviApi.getOptions("account/contacts/send", "POST", {
                type: this.contactType,
                action: this.contactAction,
                value,
                confirm_value: confirmValue,
                current_password: this.currentPassword,
            })).then(
                (response) => {
                    this.masked = response.body.masked;
                    this.contactPhase = "code";
                    this.resetContactOtp();
                    const debugCode = response.body && response.body.debug_code;
                    if (debugCode) {
                        const digits = String(debugCode).replace(/\D/g, "").slice(0, 6).split("");
                        this.contactOtpDigits = [0, 1, 2, 3, 4, 5].map((i) => digits[i] || "");
                        this.code = this.contactOtpDigits.join("");
                        this.error = `Código de desenvolvimento: ${debugCode}`;
                    }
                    this.startResend();
                    this.$nextTick(() => this.focusContactOtp(0));
                },
                (response) => {
                    const code = response.body && response.body.message;
                    const map = {
                        taken: "users-profile-taken",
                        wrong_password: "users-profile-wrong_password",
                        too_many_attempts: "users-profile-too_many_attempts",
                        mismatch: "users-profile-contact_mismatch",
                        invalid_phone: "users-profile-invalid_phone",
                        invalid_email: "users-profile-invalid_email",
                        missing_contact: "users-profile-missing_contact",
                        send_failed: "users-profile-send_failed",
                        too_many: "users-profile-too_many",
                    };
                    const key = map[code] || (response.status === 429 ? "users-profile-too_many_attempts" : "users-profile-send_failed");
                    // Na fase do código o erro aparece embaixo dos dígitos (reenvio que falhou).
                    this.error = this.t(key);
                }
            );
        },
        startResend() {
            this.resendIn = 60;
            clearInterval(this.resendTimer);
            this.resendTimer = setInterval(() => {
                this.resendIn -= 1;
                if (this.resendIn <= 0) clearInterval(this.resendTimer);
            }, 1000);
        },
        resetContactOtp() {
            this.contactOtpDigits = ["", "", "", "", "", ""];
            this.code = "";
            this.codeError = false;
            this.contactOtpFocus = 0;
        },
        focusContactOtp(index) {
            const ref = this.$refs["contactOtp" + index];
            const el = Array.isArray(ref) ? ref[0] : ref;
            if (el && el.focus) el.focus();
        },
        syncContactCode() {
            this.code = this.contactOtpDigits.join("");
        },
        onContactOtpInput(event, index) {
            const raw = String(event.target.value || "").replace(/\D/g, "");
            const value = raw.slice(-1);
            this.$set(this.contactOtpDigits, index, value);
            event.target.value = value;
            this.codeError = false;
            this.syncContactCode();
            if (value && index < 5) this.focusContactOtp(index + 1);
        },
        onContactOtpKeydown(event, index) {
            if (event.key === "Backspace" && !this.contactOtpDigits[index] && index > 0) {
                this.focusContactOtp(index - 1);
            }
        },
        onContactOtpPaste(event) {
            event.preventDefault();
            const pasted = ((event.clipboardData || window.clipboardData).getData("text") || "").replace(/\D/g, "").slice(0, 6);
            if (!pasted) return;
            const next = ["", "", "", "", "", ""];
            for (let i = 0; i < pasted.length; i += 1) next[i] = pasted[i];
            this.contactOtpDigits = next;
            this.codeError = false;
            this.syncContactCode();
            this.focusContactOtp(Math.min(pasted.length, 5));
        },
        confirmContact() {
            if (this.contactSubmitting) return;
            this.syncContactCode();
            if (this.code.length !== 6) {
                return;
            }
            this.codeError = false;
            this.error = "";
            this.contactSubmitting = true;
            this.$http(keviApi.getOptions("account/contacts/confirm", "POST", {
                type: this.contactType,
                code: this.code,
            })).then(
                (response) => {
                    this.contactSubmitting = false;
                    this.profile = response.body;
                    this.$refs.contactModal.close();
                    const email = this.contactType === "email";
                    const changed = this.contactAction === "change";
                    let title;
                    let body;
                    if (email && changed) {
                        title = this.t("users-profile-email_changed_toast");
                        body = this.t("users-profile-email_changed_toast_body");
                    } else if (email) {
                        title = this.t("users-profile-email_toast");
                        body = this.t("users-profile-email_toast_body");
                    } else if (changed) {
                        title = this.t("users-profile-phone_changed_toast");
                        body = this.t("users-profile-phone_changed_toast_body");
                    } else {
                        title = this.t("users-profile-phone_toast");
                        body = this.t("users-profile-phone_toast_body");
                    }
                    this.$store.dispatch("notification/success", title + ". " + body);
                },
                (response) => {
                    this.contactSubmitting = false;
                    const code = response.body && response.body.message;
                    if (response.status === 400 && (code === "invalid_code" || !code)) {
                        this.codeError = true;
                        this.codeErrorMessage = this.t("users-profile-invalid_code");
                    } else if (code === "too_many_attempts" || code === "expired") {
                        this.codeError = true;
                        this.codeErrorMessage = this.t(code === "expired" ? "users-profile-code_expired" : "users-profile-code_too_many");
                    } else {
                        this.error = this.t("users-profile-generic_error");
                    }
                }
            );
        },
        openPassword() {
            this.error = "";
            this.currentPassword = "";
            this.newPassword = "";
            this.repeatPassword = "";
            this.showPassword = false;
            this.$refs.passwordModal.open();
        },
        savePassword() {
            if (this.newPassword.length < 8) {
                this.error = this.t("users-profile-password_rule");
                return;
            }
            if (this.newPassword !== this.repeatPassword) {
                this.error = this.t("users-profile-mismatch");
                return;
            }
            this.error = "";
            this.$http(keviApi.getOptions("account/password", "POST", {
                current_password: this.currentPassword,
                new_password: this.newPassword,
                new_password_repeat: this.repeatPassword,
            })).then(
                () => {
                    this.$refs.passwordModal.close();
                    this.$store.dispatch("notification/success", this.t("users-profile-password_toast"));
                    this.load();
                },
                (response) => {
                    const code = response.body && response.body.message;
                    if (code === "weak_password") this.error = this.t("users-profile-password_rule");
                    else if (code === "mismatch") this.error = this.t("users-profile-mismatch");
                    else this.error = this.passwordError(response, "users-password_modal-failed");
                }
            );
        },
        openTwofa(step) {
            this.twofaStep = step || "password";
            this.grantToken = "";
            this.qrImage = "";
            this.secret = "";
            this.backupCodes = [];
            this.twofaSubmitting = false;
            this.error = "";
            this.codeError = false;
            this.currentPassword = "";
            this.showPassword = false;
            this.resetOtp();
            this.$nextTick(() => this.$refs.twofaModal && this.$refs.twofaModal.open());
        },
        restartTwofa() {
            this.openTwofa("password");
        },
        confirmForTwofa() {
            if (this.twofaSubmitting) return;
            this.error = "";
            this.twofaSubmitting = true;
            this.$http(keviApi.getOptions("account/confirm-password", "POST", {
                current_password: this.currentPassword,
            })).then(
                (response) => {
                    this.grantToken = (response.body && response.body.grant_token) || "";
                    this.generateQr();
                },
                (response) => {
                    this.twofaSubmitting = false;
                    this.error = this.passwordError(response);
                }
            );
        },
        generateQr() {
            this.$http(keviApi.getOptions("2fa/generate-qrcode", "POST", {
                grant_token: this.grantToken,
            })).then(
                (response) => {
                    this.twofaSubmitting = false;
                    this.qrImage = response.body.qr_image || "";
                    this.secret = response.body.secret || "";
                    this.twofaStep = "qr";
                },
                () => {
                    // Grant vencido ou ausente: recomeça pela senha.
                    this.twofaSubmitting = false;
                    this.restartTwofaWithError(this.t("users-profile-twofa_restart"));
                }
            );
        },
        restartTwofaWithError(message) {
            this.openTwofa("password");
            this.error = message;
        },
        goToCodeStep() {
            this.resetOtp();
            this.twofaStep = "code";
            this.$nextTick(() => this.focusOtp(0));
        },
        resetOtp() {
            this.otpDigits = ["", "", "", "", "", ""];
            this.twofaCode = "";
            this.codeError = false;
            this.otpFocus = 0;
        },
        focusOtp(index) {
            const ref = this.$refs["otp" + index];
            const el = Array.isArray(ref) ? ref[0] : ref;
            if (el && el.focus) el.focus();
        },
        syncOtpCode() {
            this.twofaCode = this.otpDigits.join("");
        },
        onOtpInput(event, index) {
            const raw = String(event.target.value || "").replace(/\D/g, "");
            const value = raw.slice(-1);
            this.$set(this.otpDigits, index, value);
            event.target.value = value;
            this.codeError = false;
            this.syncOtpCode();
            if (value !== "" && index < 5) this.focusOtp(index + 1);
        },
        onOtpKeydown(event, index) {
            if (event.key === "Backspace" && !this.otpDigits[index] && index > 0) {
                this.focusOtp(index - 1);
            }
        },
        onOtpPaste(event) {
            event.preventDefault();
            const pasted = ((event.clipboardData || window.clipboardData).getData("text") || "").replace(/\D/g, "").slice(0, 6);
            if (!pasted) return;
            const next = ["", "", "", "", "", ""];
            for (let i = 0; i < pasted.length; i += 1) next[i] = pasted[i];
            this.otpDigits = next;
            this.codeError = false;
            this.syncOtpCode();
            this.focusOtp(Math.min(pasted.length, 5));
        },
        activateTwofa() {
            this.syncOtpCode();
            if (!this.canActivateTwofa) return;
            const code = String(this.twofaCode || "").replace(/\D+/g, "");
            this.codeError = false;
            this.twofaSubmitting = true;
            this.$http(keviApi.getOptions("2fa/usercode-validate", "POST", { code })).then(
                (response) => {
                    this.twofaSubmitting = false;
                    if (this.profile) this.$set(this.profile, "twofa_enabled", true);
                    this.backupCodes = (response.body && response.body.backupCodes) || [];
                    this.twofaStep = "codes";
                },
                (response) => {
                    this.twofaSubmitting = false;
                    const message = response.body && response.body.message;
                    if (message === "too_many_attempts" || message === "setup_expired") {
                        this.restartTwofaWithError(this.t(message === "too_many_attempts" ? "users-profile-twofa_too_many" : "users-profile-twofa_restart"));
                    } else if (response.status === 400) {
                        this.codeError = true;
                    } else {
                        this.codeError = true;
                    }
                }
            );
        },
        finishTwofa() {
            this.$refs.twofaModal.close();
            this.load();
        },
        disableTwofa() {
            this.$http(keviApi.getOptions("2fa/disable", "POST", {
                current_password: this.currentPassword,
            })).then(
                () => {
                    this.twofaStep = "disabled";
                    this.load();
                    this.$store.dispatch("notification/success", this.t("users-profile-disabled_toast") + " " + this.t("users-profile-disabled_toast_body"));
                },
                (response) => { this.error = this.passwordError(response); }
            );
        },
        copy(text) {
            if (!navigator.clipboard) return;
            navigator.clipboard.writeText(text).then(() => {
                this.copied = true;
                clearTimeout(this.copiedTimer);
                this.copiedTimer = setTimeout(() => { this.copied = false; }, 2000);
            }).catch(() => {});
        },
        downloadCodes() {
            const blob = new Blob([this.backupCodes.join("\n")], { type: "text/plain" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "kevi-recovery-codes.txt";
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 0);
        },
        logout() {
            this.$refs.confirmLogout.open();
        },
    },
};
</script>

<style scoped>
.profile-page {
    min-height: calc(100vh - 40px);
    background: #f9f9fb;
    padding: 16px 24px 32px;
    margin: -20px -20px 0;
    box-sizing: border-box;
}
.profile-sheet {
    width: min(588px, 100%);
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    box-sizing: border-box;
}
.profile-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    box-sizing: border-box;
}
.icon-btn {
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    padding: 8px;
}
.icon-btn:hover { background: #f3f3f6; }
.head-spacer { width: 36px; height: 36px; flex: none; }
.identity {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
}
.identity-text { min-width: 0; }
.avatar-btn {
    position: relative;
    width: 64px;
    height: 64px;
    border: 0;
    padding: 0;
    border-radius: 999px;
    background: #e7faec;
    cursor: pointer;
    overflow: visible;
    flex: none;
}
.avatar-btn > img,
.avatar-btn > .initials {
    width: 64px;
    height: 64px;
    border-radius: 999px;
    object-fit: cover;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: "Sora", sans-serif;
    font-size: 18px;
    color: #626473;
    background: #e7faec;
}
.pencil {
    position: absolute;
    right: -2px;
    bottom: -2px;
    width: 24px;
    height: 24px;
    border-radius: 999px;
    background: #fafafc;
    border: 1px solid #e5e6eb;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
}
.sr { display: none; }
.kicker {
    margin: 0;
    color: #9598a6;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.2;
}
.profile-head h1 {
    margin: 12px 0 0;
    font-family: "Sora", sans-serif;
    font-size: 24px;
    font-weight: 400;
    line-height: 1.3;
    color: #383c50;
}
.card {
    background: #fff;
    border: 1px solid #e5e6eb;
    border-radius: 12px;
    overflow: hidden;
}
.card-head {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
}
.card-head.tight {
    gap: 8px;
    padding: 8px 0;
}
.card h2,
.card h3 {
    margin: 0;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #1f2337;
    line-height: 1.2;
}
.security-head {
    display: flex;
    align-items: flex-start;
    gap: 16px;
}
.security-head h2 {
    font-family: "Sora", sans-serif;
    font-size: 18px;
    font-weight: 400;
    line-height: 1.3;
    color: #383c50;
}
.security-card {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 20px;
}
.help {
    margin: 0;
    color: #626473;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.2;
}
.divider {
    height: 0;
    border: 0;
    border-top: 1px solid #e5e6eb;
    margin: 0;
}
.recovery {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin: 0;
    padding: 16px 20px;
    box-sizing: border-box;
}
.row.flush {
    padding-top: 0;
    padding-bottom: 0;
    padding-left: 0;
    padding-right: 0;
}
.personal-card .row {
    padding: 16px 20px;
}
.logout-card .row,
.logout-card .row.flush {
    padding: 18px 20px;
}
.row-main {
    display: flex;
    gap: 16px;
    align-items: center;
    min-width: 0;
    flex: 1;
}
.info {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
}
.title-line {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.row strong {
    margin: 0;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #383c50;
    line-height: 1.2;
}
.row p {
    margin: 0;
    color: #383c50;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.2;
}
.row p.muted { color: #9598a6; }
.row p.two-lines { white-space: pre-line; }
.row strong + em,
.title-line em {
    font-style: normal;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.2;
    border-radius: 999px;
    padding: 4px 8px;
    box-sizing: border-box;
}
em.ok { color: #0b9055; background: #d3f8e0; }
em.wait { color: #9d530f; background: #fffbed; }
em.off { color: #f04438; background: #fef3f2; }
.warning {
    background: #fffbed;
    border: 0;
    border-radius: 8px;
    padding: 12px;
}
.warning strong {
    display: block;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    color: #9d530f;
    margin: 0 0 8px;
}
.warning p {
    margin: 0;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.2;
    color: #f79009;
}
.note {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    color: #9598a6;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    line-height: 1.5;
}
.note img { flex: none; }
.actions { display: flex; gap: 8px; flex: none; flex-wrap: wrap; justify-content: flex-end; }
.ghost,
.primary {
    min-height: 32px;
    box-sizing: border-box;
    border-radius: 8px;
    padding: 8px 12px;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.2;
    cursor: pointer;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}
.ghost {
    border: 1px solid #e5e6eb;
    background: #fafafc;
    color: #383c50;
}
.ghost.with-icon img { flex: none; display: block; }
.ghost:hover { border-color: #bbbdc7; }
.primary {
    border: 0;
    background: #2aaa4c;
    color: #f1fcf3;
}
.primary:hover { background: #249642; }
.row-icon {
    width: 20px;
    height: 20px;
    flex: none;
    display: block;
    object-fit: contain;
}
.form-error { color: #d92d20; font-size: 13px; margin: 0; }
.photo-pass {
    display: flex;
    gap: 8px;
    align-items: center;
    font-size: 13px;
    color: #383c50;
}
.photo-pass input {
    height: 36px;
    border: 1px solid #e5e6eb;
    border-radius: 8px;
    padding: 0 10px;
}
label { display: block; margin-top: 8px; }

.twofa-host >>> .kv-modal-header,
.twofa-host >>> .kv-modal-footer {
    display: none;
}
.twofa-host >>> .kv-modal-wrapper.kv-modal-small {
    max-width: 560px;
}
.twofa-host >>> .kv-modal-dialog {
    background: #fff;
    border: 1px solid #e5e6eb;
    border-radius: 16px;
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.08);
}
.twofa-host >>> .kv-modal-body {
    padding: 16px;
}
.twofa-flow {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}
.twofa-head {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
    text-align: center;
}
.twofa-badge {
    width: 40px;
    height: 40px;
    border-radius: 999px;
    background: #d3f8e0;
    display: flex;
    align-items: center;
    justify-content: center;
}
.twofa-badge img {
    width: 20px;
    height: 20px;
    display: block;
}
.twofa-badge.is-danger {
    background: #fef3f2;
}
.twofa-warning {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    background: #fef3f2;
    color: #b42318;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    line-height: 1.3;
    text-align: left;
}
.twofa-warning img {
    width: 16px;
    height: 16px;
    flex: none;
}
.twofa-step {
    margin: 0;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.3;
    color: #9598a6;
}
.twofa-title {
    margin: 0;
    font-family: "Sora", sans-serif;
    font-size: 20px;
    font-weight: 400;
    line-height: 1.3;
    color: #1f2337;
}
.twofa-subtitle {
    margin: 0;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.2;
    color: #626473;
}
.twofa-field {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.twofa-field label {
    margin: 0;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.2;
    color: #383c50;
}
.twofa-input {
    display: flex;
    align-items: center;
    height: 44px;
    padding: 0 12px;
    border: 1px solid #e5e6eb;
    border-radius: 8px;
    background: #f9f9fb;
    box-sizing: border-box;
}
.contact-input:focus-within {
    border-color: #bbbdc7;
    background: #fff;
}
.twofa-input input {
    flex: 1;
    min-width: 0;
    border: 0;
    background: transparent;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    color: #1f2337;
    outline: none;
}
.twofa-eye {
    border: 0;
    background: transparent;
    padding: 0;
    width: 20px;
    height: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}
.twofa-eye img {
    width: 20px;
    height: 20px;
    display: block;
}
.twofa-qr {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
}
.twofa-qr-frame {
    width: 100%;
    display: flex;
    justify-content: center;
    background: #fff;
}
.twofa-qr-frame img {
    width: 132px;
    height: 132px;
    display: block;
}
.twofa-manual-help {
    margin: 0;
    width: 100%;
    text-align: center;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    line-height: 1.3;
    color: #626473;
}
.twofa-secret {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 8px 8px 12px;
    border: 1px solid #e5e6eb;
    border-radius: 8px;
    background: #f9f9fb;
    box-sizing: border-box;
}
.twofa-secret span {
    flex: 1;
    min-width: 0;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
    color: #1f2337;
    word-break: break-all;
}
.twofa-link {
    border: 0;
    background: transparent;
    padding: 4px 7px;
    min-height: 28px;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    color: #383c50;
    cursor: pointer;
}
.twofa-otp-wrap {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}
.twofa-otp {
    display: flex;
    gap: 8px;
    justify-content: center;
    flex-wrap: wrap;
}
.twofa-otp-digit {
    width: 56px;
    height: 56px;
    box-sizing: border-box;
    border: 1px solid #e5e6eb;
    border-radius: 8px;
    background: #fff;
    text-align: center;
    font-family: "Sora", sans-serif;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3;
    color: #1f2337;
    outline: none;
}
.twofa-otp-digit::placeholder {
    color: #bbbdc7;
}
.twofa-otp-digit.is-active,
.twofa-otp-digit:focus {
    border: 1.5px solid #2aaa4c;
}
.twofa-otp-digit.is-error {
    border: 1.5px solid #ffcdc9;
    background: #fef3f2;
    color: #de3024;
}
.twofa-hint {
    margin: 0;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    line-height: 1.3;
    color: #9598a6;
}
.twofa-error-msg {
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    line-height: 1.2;
    color: #de3024;
}
.twofa-error-msg img {
    width: 16px;
    height: 16px;
    flex: none;
}
.twofa-error-text {
    margin: 0;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    color: #de3024;
}
.twofa-codes-layout {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.twofa-codes-primary { order: 1; width: 100%; }
.twofa-codes-content {
    order: 2;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.twofa-codes-box {
    display: flex;
    gap: 24px;
    width: 100%;
    box-sizing: border-box;
    padding: 14px 16px;
    border: 1px solid #e5e6eb;
    border-radius: 8px;
    background: #f9f9fb;
}
.twofa-codes-col {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.twofa-codes-col span {
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
    color: #1f2337;
}
.twofa-codes-actions {
    display: flex;
    gap: 8px;
    width: 100%;
}
.twofa-footer {
    width: 100%;
    display: flex;
    gap: 8px;
}
.twofa-footer-stack {
    flex-direction: column;
}
.twofa-footer-disabled {
    flex-direction: row;
}
.twofa-btn {
    flex: 1;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 12px;
    border-radius: 8px;
    font-family: "Inter", sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.2;
    cursor: pointer;
    box-sizing: border-box;
}
.twofa-btn-sm {
    min-height: 32px;
    padding: 8px 12px;
    font-size: 14px;
}
.twofa-btn-sm img {
    width: 16px;
    height: 16px;
    display: block;
}
.twofa-btn-ghost {
    border: 1px solid #e5e6eb;
    background: #fafafc;
    color: #383c50;
}
.twofa-btn-primary {
    border: 0;
    background: #2aaa4c;
    color: #f1fcf3;
}
.twofa-btn-primary.is-disabled,
.twofa-btn-primary:disabled {
    background: #f3f3f6;
    color: #9598a6;
    cursor: default;
}
.twofa-btn-danger {
    border: 0;
    background: #fef3f2;
    color: #f04438;
}
.twofa-btn-danger:hover {
    background: #fee4e2;
}

.contact-host >>> .kv-modal-wrapper.kv-modal-small,
.account-form-host >>> .kv-modal-wrapper.kv-modal-small {
    max-width: 560px;
}
.contact-host >>> .kv-modal-dialog,
.account-form-host >>> .kv-modal-dialog {
    background: #fff;
    border: 1px solid #e5e6eb;
    border-radius: 16px;
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.08);
}
.contact-host >>> .kv-modal-body,
.account-form-host >>> .kv-modal-body {
    padding: 16px;
}
.contact-host >>> .kv-modal-header,
.contact-host >>> .kv-modal-footer,
.account-form-host >>> .kv-modal-header,
.account-form-host >>> .kv-modal-footer {
    display: none;
}
.account-form-flow {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}
.account-form-head {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
    text-align: center;
}
.contact-flow {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}
.contact-head {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
    text-align: center;
}
.contact-badge {
    width: 40px;
    height: 40px;
    border-radius: 999px;
    background: #d3f8e0;
    display: flex;
    align-items: center;
    justify-content: center;
}
.contact-badge img {
    width: 20px;
    height: 20px;
    display: block;
}
.contact-title {
    margin: 0;
    font-family: "Sora", sans-serif;
    font-size: 20px;
    font-weight: 400;
    line-height: 1.3;
    color: #1f2337;
}
.contact-subtitle {
    margin: 0;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.2;
    color: #626473;
}
.contact-fields {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.contact-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
}
.contact-field label {
    margin: 0;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.2;
    color: #383c50;
}
.contact-input {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 44px;
    padding: 0 12px;
    border: 1px solid #e5e6eb;
    border-radius: 8px;
    background: #f9f9fb;
    box-sizing: border-box;
}
.contact-input:focus-within {
    border-color: #bbbdc7;
    background: #fff;
}
.contact-input-icon {
    width: 20px;
    height: 20px;
    flex: none;
}
.contact-input input {
    flex: 1;
    min-width: 0;
    border: 0;
    background: transparent;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    color: #1f2337;
    outline: none;
}
.contact-input input::placeholder {
    color: #9598a6;
}
.contact-eye {
    border: 0;
    background: transparent;
    padding: 0;
    width: 20px;
    height: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}
.contact-eye img {
    width: 20px;
    height: 20px;
    display: block;
}
.contact-otp-wrap {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}
.contact-otp {
    display: flex;
    gap: 8px;
    justify-content: center;
    flex-wrap: wrap;
}
.contact-otp-digit {
    width: 56px;
    height: 56px;
    box-sizing: border-box;
    border: 1px solid #e5e6eb;
    border-radius: 8px;
    background: #fff;
    text-align: center;
    font-family: "Sora", sans-serif;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3;
    color: #1f2337;
    outline: none;
}
.contact-otp-digit::placeholder {
    color: #bbbdc7;
}
.contact-otp-digit.is-active,
.contact-otp-digit:focus {
    border: 1.5px solid #2aaa4c;
}
.contact-otp-digit.is-error {
    border: 1.5px solid #ffcdc9;
    background: #fef3f2;
    color: #de3024;
}
.contact-hint {
    margin: 0;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    line-height: 1.3;
    color: #9598a6;
}
.contact-resend {
    border: 0;
    background: transparent;
    padding: 0;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    line-height: 1.3;
    color: #9598a6;
    cursor: pointer;
}
.contact-resend.is-link {
    color: #2aaa4c;
    font-size: 12px;
    font-weight: 500;
}
.contact-error-msg {
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    line-height: 1.3;
    color: #de3024;
}
.contact-error-msg img {
    width: 16px;
    height: 16px;
    flex: none;
}
.contact-error-text {
    margin: 0;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    color: #de3024;
}
.contact-footer {
    width: 100%;
    display: flex;
    gap: 8px;
}
.contact-btn {
    flex: 1;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 12px;
    border-radius: 8px;
    font-family: "Inter", sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.2;
    cursor: pointer;
    box-sizing: border-box;
}
.contact-btn-ghost {
    border: 1px solid #e5e6eb;
    background: #fafafc;
    color: #383c50;
}
.contact-btn-primary {
    border: 0;
    background: #2aaa4c;
    color: #f1fcf3;
}
.contact-btn-primary.is-disabled,
.contact-btn-primary:disabled {
    background: #f3f3f6;
    color: #9598a6;
    cursor: default;
}

@media (max-width: 700px) {
    .profile-page { margin: 0; padding: 16px 16px 32px; }
    .profile-head { padding: 16px 0; }
    .head-spacer { display: none; }
    .identity { flex: 1; }
    .row { align-items: flex-start; flex-direction: column; }
    .row-main { flex-direction: column; align-items: flex-start; }
    .actions { justify-content: flex-start; }
    .row > .ghost,
    .row > .primary,
    .actions .ghost,
    .actions .primary { width: auto; flex: none; }
    .logout-card .row { flex-direction: row; align-items: center; }
    .logout-card .ghost { width: auto; }
    .twofa-host >>> .kv-modal,
    .contact-host >>> .kv-modal,
    .account-form-host >>> .kv-modal {
        align-items: flex-end;
    }
    .twofa-host >>> .kv-modal-wrapper.kv-modal-small,
    .contact-host >>> .kv-modal-wrapper.kv-modal-small,
    .account-form-host >>> .kv-modal-wrapper.kv-modal-small {
        max-width: 100%;
        width: 100%;
        max-height: 100%;
    }
    .twofa-host >>> .kv-modal-dialog,
    .contact-host >>> .kv-modal-dialog,
    .account-form-host >>> .kv-modal-dialog {
        border-radius: 16px 16px 0 0;
        border-bottom: 0;
    }
    .twofa-footer-disabled { flex-direction: column; }
    .twofa-otp-digit {
        width: 48px;
        height: 48px;
        font-size: 16px;
    }
    .twofa-codes-primary { order: 2; }
    .twofa-codes-content { order: 1; }
    .contact-otp {
        flex-wrap: nowrap;
        width: 100%;
    }
    .contact-otp-digit {
        flex: 1;
        width: auto;
        min-width: 0;
        height: 56px;
        font-size: 16px;
    }
}
</style>
